import { useEffect, useRef } from 'react';

interface GaugeCanvasProps {
  speed: number;
  maxSpeed?: number;
}

export function GaugeCanvas({ speed, maxSpeed = 1000 }: GaugeCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const currentAngleRef = useRef<number>(Math.PI);
  const targetAngleRef = useRef<number>(Math.PI);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const CX = 260, CY = 290, R = 230;
    const START_ANGLE = Math.PI;
    const END_ANGLE = Math.PI * 2;

    const speedToAngle = (s: number) =>
      START_ANGLE + Math.min(s, maxSpeed) / maxSpeed * Math.PI;

    targetAngleRef.current = speedToAngle(speed);

    const animate = () => {
      const diff = targetAngleRef.current - currentAngleRef.current;
      if (Math.abs(diff) > 0.002) {
        currentAngleRef.current += diff * 0.1;
        animationRef.current = requestAnimationFrame(animate);
      } else {
        currentAngleRef.current = targetAngleRef.current;
      }

      // Clear
      ctx.clearRect(0, 0, 520, 320);

      // Track background
      ctx.beginPath();
      ctx.arc(CX, CY, R, START_ANGLE, END_ANGLE);
      ctx.strokeStyle = 'rgba(16, 28, 42, 1)';
      ctx.lineWidth = 28;
      ctx.lineCap = 'round';
      ctx.stroke();

      // Filled arc with gradient
      if (currentAngleRef.current > START_ANGLE) {
        const grad = ctx.createLinearGradient(CX - R, CY, CX + R, CY);
        grad.addColorStop(0, 'hsl(var(--st-cyan))');
        grad.addColorStop(0.6, '#0088ff');
        grad.addColorStop(1, 'hsl(var(--st-pink))');

        ctx.save();
        ctx.shadowBlur = 24;
        ctx.shadowColor = 'hsla(var(--st-cyan) / 0.5)';
        ctx.beginPath();
        ctx.arc(CX, CY, R, START_ANGLE, currentAngleRef.current);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 28;
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.restore();
      }

      // Needle
      ctx.save();
      ctx.shadowBlur = 16;
      ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
      const nx = CX + Math.cos(currentAngleRef.current) * (R - 8);
      const ny = CY + Math.sin(currentAngleRef.current) * (R - 8);
      const bx = CX + Math.cos(currentAngleRef.current + Math.PI) * 20;
      const by = CY + Math.sin(currentAngleRef.current + Math.PI) * 20;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(nx, ny);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3.5;
      ctx.lineCap = 'round';
      ctx.stroke();
      ctx.restore();

      // Center circle
      ctx.beginPath();
      ctx.arc(CX, CY, 11, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(CX, CY, 5, 0, Math.PI * 2);
      ctx.fillStyle = 'hsl(var(--st-bg))';
      ctx.fill();
    };

    animate();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [speed, maxSpeed]);

  return <canvas ref={canvasRef} width={520} height={320} className="w-full max-w-sm" />;
}
